import {
  FC,
  Children,
  Fragment,
  ReactNode,
  ReactElement,
  useId,
  isValidElement,
  cloneElement,
} from 'react';
import { styled, HStack, Box } from '@mildang/styled-system/jsx';
import { field, FieldVariantProps } from '@mildang/styled-system/recipes';
import { PandaHTMLStyledProps } from '../../../types/HTMLStyledProps';
import { FormLabel } from '../FormLabel';
import HelpText from '../HelpText';
import type { HelpTextVariantLiteral } from '../HelpText/constants';

// recipe(field)만 적용된 순수 컨테이너. 선언형 prop 없이 children을 그대로 감쌀 때 사용(하위 호환).
const StyledField = styled('div', field) as FC<
  FieldVariantProps & PandaHTMLStyledProps<'div'>
>;

const hasSlotContent = (node: ReactNode): boolean =>
  Children.toArray(node).some((child) => {
    if (typeof child === 'string') return child.length > 0;
    if (isValidElement(child) && child.type === Fragment) {
      return hasSlotContent((child.props as { children?: ReactNode }).children);
    }
    return true;
  });

export type FieldProps = FieldVariantProps &
  PandaHTMLStyledProps<'div'> & {
    /** 필드 라벨(Figma form_title_item Title / show_title). 주면 FormLabel을 컨트롤 위에 자동 배치. */
    label?: ReactNode;
    /** 필수 표시(별표). Figma essential. */
    required?: boolean;
    /** 라벨 보조 설명(Figma Description / show_description). */
    description?: ReactNode;
    /** 컨트롤 아래 도움말(Figma help_text / show_error_text). */
    helpText?: ReactNode;
    /** 도움말 종류(Figma help_text.type). info/warning/error/link/success. */
    helpVariant?: HelpTextVariantLiteral;
    /** 도움말 아이콘 표시(Figma help_text.icon). */
    helpIcon?: boolean;
    /**
     * 컨트롤/라벨 연결에 쓸 id.
     * - 미지정: Field가 id를 생성해 라벨↔단일 컨트롤을 **자동 연결**(children에 id/aria 주입).
     * - 명시: "외부(위임 래퍼 등)가 컨트롤 id를 직접 관리한다"는 신호 → 자동주입을 끄고 라벨 htmlFor에만 사용.
     *   (adornment로 컨트롤이 wrapper에 감싸인 경우 자동주입이 wrapper에 붙는 문제를 피함.)
     */
    htmlFor?: string;
    /**
     * 상단 우측 슬롯. 토글·입력·버튼·텍스트·툴팁 등 임의의 ReactNode를 배치함.
     * children이 없고 단일 element이면 이 요소를 대표 컨트롤로 보고 라벨 id를 자동 연결함.
     */
    sideContent?: ReactNode;
    /** 본문 슬롯. 주로 폼 컨트롤을 두지만 표·카드 등 임의의 ReactNode를 배치할 수 있음. */
    children?: ReactNode;
  };

/**
 * 폼 필드 컨테이너. Figma `field`와 동일 구조 — 라벨/설명/도움말은 정해진 자리(prop 토글),
 * 상단 우측은 sideContent, 본문은 children으로 받는다.
 *
 * - `label`/`description`/`helpText` prop을 주면 FormLabel(위) + children(컨트롤) + HelpText(아래)를 자동 배치.
 *   이때 id를 생성해 FormLabel `htmlFor` ↔ 단일 컨트롤 `id`/`aria-describedby`를 **자동 연결**한다.
 *   (컨트롤은 순수하게 두고 Field가 접근성 속성을 주입 — shadcn FormControl 방식.)
 * - 선언형 prop을 하나도 안 주면 기존처럼 children을 그대로 렌더(하위 호환) —
 *   `<Field><FormLabel/><Input/><HelpText/></Field>` 수동 조합도 그대로 동작.
 * - 값/검증(TanStack Form)은 별개 축 — 이 컴포넌트는 순수 레이아웃/프레젠테이션만 담당한다.
 */
export const Field: FC<FieldProps> = (props) => {
  const {
    label,
    required,
    description,
    helpText,
    helpVariant,
    helpIcon,
    htmlFor,
    sideContent,
    children,
    ...rest
  } = props;

  // hooks는 조건부 return 위에서 항상 호출.
  const autoId = useId();

  // 선언형 prop이 하나도 없으면 순수 컨테이너로(기존 동작 보존).
  const hasChildren = hasSlotContent(children);
  const hasSideContent = hasSlotContent(sideContent);
  const isDeclarative =
    label != null || description != null || helpText != null || hasSideContent;
  if (!isDeclarative) {
    return <StyledField {...rest}>{children}</StyledField>;
  }

  // children이 있으면 본문, 없으면 sideContent를 대표 컨트롤로 본다.
  // htmlFor 미지정일 때만 그 단일 element에 id/aria를 자동 주입한다.
  // htmlFor 명시 = 외부가 컨트롤 id를 직접 관리 → 자동주입 끔(wrapper 오주입 방지).
  const primaryControl = hasChildren ? children : hasSideContent ? sideContent : undefined;
  const canAutoLink =
    htmlFor == null &&
    isValidElement(primaryControl) &&
    primaryControl.type !== Fragment;
  const controlProps = canAutoLink
    ? (primaryControl.props as { id?: string; 'aria-describedby'?: string })
    : undefined;
  // 컨트롤이 이미 가진 id가 라벨 연결의 정본이다. autoId를 htmlFor에 쓰면서 clone 시 기존 id를
  // 보존하면 label[for]와 실제 control id가 갈라진다.
  const controlId = htmlFor ?? controlProps?.id ?? (canAutoLink ? autoId : undefined);
  const helpId = helpText != null && controlId != null ? `${controlId}-help` : undefined;
  const describedBy = [controlProps?.['aria-describedby'], helpId].filter(Boolean).join(' ') || undefined;
  const linkedPrimaryControl =
    canAutoLink
      ? cloneElement(primaryControl as ReactElement<Record<string, unknown>>, {
          id: controlId,
          'aria-describedby': describedBy,
        })
      : primaryControl;

  const content = hasChildren ? linkedPrimaryControl : undefined;
  const side = hasChildren
    ? hasSideContent
      ? sideContent
      : undefined
    : linkedPrimaryControl;

  const labelNode =
    label != null ? (
      <FormLabel htmlFor={controlId} required={required} description={description}>
        {label}
      </FormLabel>
    ) : undefined;

  const helpNode =
    helpText != null ? (
      <HelpText id={helpId} variant={helpVariant} showIcon={helpIcon}>
        {helpText}
      </HelpText>
    ) : undefined;

  return (
    <StyledField {...rest}>
      {(labelNode != null || side != null) && (
        <HStack width="100%" alignItems="center" gap="8">
          <Box flex="1" minWidth="0">
            {labelNode}
          </Box>
          {side != null && <Box flexShrink="0">{side}</Box>}
        </HStack>
      )}
      {content != null && (
        <Box width="100%" minWidth="0" display="flex" flexDirection="column" gap="8">
          {content}
        </Box>
      )}
      {helpNode != null && (
        <Box width="100%" minWidth="0">
          {helpNode}
        </Box>
      )}
    </StyledField>
  );
};
